// Fasting groups: one member's own clock. Phase 1 of docs/plan-fasting-groups.md.
//
// WHAT THE APP ALREADY HAD, and why this is a new layer rather than a new type.
// `fasting` has been a registered event type since the leaf split: multi-day,
// `auto_at_end`, with a group countdown from the event's start to its end. What
// it has no concept of is that EVERY MEMBER STARTS AT A DIFFERENT TIME. One
// group fast is a shared target; the thing a person actually looks at is their
// own elapsed hours. That per-member clock is `fast_sessions`, and everything in
// this file is the pure part of reading it.
//
// ⚠ A SESSION BELONGS TO A GROUP AND *OPTIONALLY* TO AN EVENT, decided
// 2026-09-10. A member can start a fast whenever they like; a group fasting
// event just gives everybody something to hang theirs on. That is what makes
// the multi-week PREP period in the plan free: prep is sessions with no event,
// rather than twenty-one nested child events and the parent/child event model
// the plan says would be needed first.
//
// ⚠ AND THE STAGE COPY BELOW IS WILL'S OWN, FRAMED AS RANGES ON PURPOSE.
// Fasting physiology varies enormously between people -- somebody fat-adapted
// from keto reaches ketosis in hours, somebody coming off a carb-heavy diet
// takes a day and feels awful doing it. The plan states the stages as ranges
// rather than guarantees, and that framing is load-bearing rather than
// decorative: this is a group coordination app, not a medical device, and a
// dial that says "you are now in ketosis" would be asserting something it
// cannot know. Every string here hedges, and any edit to them should keep
// hedging.
export const KETOSIS_LEVELS = ['none', 'moderate', 'deep'];
/**
 * What to call each level, and what it means.
 *
 * ⚠ THREE LEVELS, DELIBERATELY, and this is a product decision from the plan
 * rather than a simplification for the schema. Ketone strips are read against a
 * printed colour gradient that is genuinely hard to judge -- people disagree
 * with themselves between readings. Three buckets somebody can pick with
 * confidence beat a number they are guessing at, and the whole use of this
 * value here is to nudge the dial's timeline, which does not need precision.
 */
export const KETOSIS_LABELS = {
    none: {
        label: 'Not in ketosis',
        help: 'No trace on a strip, or you have not tested.',
    },
    moderate: {
        label: 'Moderate',
        help: 'A clear reading. The usual place to be a day or so in.',
    },
    deep: {
        label: 'Deep',
        help: 'A strong reading. Common if you are already fat adapted.',
    },
};
export const FAST_END_REASONS = [
    'planned',
    'illness',
    'social',
    'physiological',
    'other',
];
/**
 * Why somebody stopped, in their own words rather than as a verdict.
 *
 * ⚠ NONE OF THESE IS A FAILURE, and the copy has to carry that. The plan asks
 * for this because a member of Will's own fasting group had to break a fast for
 * a physiological reason tied to her cycle -- a real, correct, unavoidable
 * reason that a list reading "completed / gave up" would have filed under
 * giving up. An app that grades people on this is an app they stop logging in.
 */
export const FAST_END_LABELS = {
    planned: 'Reached my target',
    illness: 'Illness',
    social: 'A social event',
    physiological: 'A physiological reason',
    other: 'Something else',
};
/**
 * The stages, in Will's wording from the plan.
 *
 * The bands are contiguous so that every elapsed time lands in exactly one, and
 * the 8-18 hour band is where "around 12 hours" from the plan lives -- stated as
 * a band rather than a point because that is what it is.
 */
export const FAST_STAGES = [
    {
        key: 'digesting',
        fromHours: 0,
        toHours: 4,
        label: 'Digesting your last meal',
        detail: 'Still working through what you ate.',
    },
    {
        key: 'settling',
        fromHours: 4,
        toHours: 8,
        label: 'Blood sugar and insulin dropping',
        detail: 'The switch has not flipped yet, but it is starting to.',
    },
    {
        key: 'transitioning',
        fromHours: 8,
        toHours: 18,
        label: 'Transitioning into ketosis',
        detail: 'Usually somewhere around the twelve-hour mark, but it varies a lot.',
    },
    {
        key: 'deepening',
        fromHours: 18,
        toHours: 24,
        label: 'Deepening ketosis, rising growth hormone',
        detail: 'The part most people can feel.',
    },
    {
        key: 'autophagy',
        fromHours: 24,
        toHours: null,
        label: 'Ramping autophagy',
        detail: 'Thought to build through the second day and beyond.',
    },
];
/**
 * How much to compress the stage boundaries for somebody already fat adapted.
 *
 * ⚠ AN APPROXIMATION, AND LABELLED AS ONE EVERYWHERE IT SURFACES. The plan asks
 * for the dial's timeline to adjust to logged ketosis rather than running one
 * fixed schedule for everybody, because somebody arriving already in ketosis
 * does not spend twelve hours transitioning into it. These multipliers are a
 * plausible shape for that, not a measured one -- there is no published curve
 * here to implement, and pretending otherwise by picking precise-looking
 * numbers would be worse than picking round ones.
 */
const KETOSIS_SPEEDUP = {
    none: 1,
    moderate: 0.75,
    deep: 0.5,
};
/**
 * The stages as they apply to THIS faster, given how deep in ketosis they
 * started.
 *
 * `null` means no reading, which is treated as `none` -- the unadjusted
 * schedule. Erring towards the slower timeline is deliberate: telling somebody
 * they are further along than they are is the wrong direction to be wrong in.
 */
export function stagesFor(startingKetosis) {
    const speed = KETOSIS_SPEEDUP[startingKetosis ?? 'none'];
    if (speed === 1)
        return [...FAST_STAGES];
    return FAST_STAGES.map((s) => ({
        ...s,
        // The first boundary stays at zero however fat-adapted somebody is: a fast
        // does not begin part-way through.
        fromHours: Math.round(s.fromHours * speed * 10) / 10,
        toHours: s.toHours === null ? null : Math.round(s.toHours * speed * 10) / 10,
    }));
}
/**
 * Which stage `hours` falls in.
 *
 * Never returns null for a non-negative number: the last stage is open-ended, so
 * every elapsed time has an answer. A negative elapsed time -- a start stamped in
 * the future, which a member correcting their start time can absolutely produce --
 * resolves to the first stage rather than to nothing.
 */
export function stageAt(hours, startingKetosis = null) {
    const stages = stagesFor(startingKetosis);
    const first = stages[0] ?? FAST_STAGES[0];
    if (first === undefined)
        throw new Error('FAST_STAGES is empty');
    // ⚠ NaN and +Infinity are handled DIFFERENTLY, and the first draft lumped
    // them together under `!Number.isFinite()`. NaN means "we have no idea", which
    // is the first stage; positive infinity means "further along than any
    // boundary", which is the last one, and returning the first for it would put
    // a fast that never ends back at "digesting your last meal". Neither arises
    // from real data -- both come out of arithmetic on a malformed timestamp --
    // but they are opposite answers and only a test says which is which.
    if (Number.isNaN(hours))
        return first;
    if (hours <= first.fromHours)
        return first;
    for (const stage of stages) {
        if (stage.toHours === null)
            return stage;
        if (hours < stage.toHours)
            return stage;
    }
    return stages[stages.length - 1] ?? first;
}
/** Hours between two instants, as a float. Negative when `now` precedes the start. */
export function elapsedHours(startedAtMs, nowMs) {
    return (nowMs - startedAtMs) / 3_600_000;
}
/**
 * "16h 42m". The string on the dial.
 *
 * ⚠ Minutes matter and seconds do not. Somebody watching hour fifteen wants to
 * see it move; a second hand on a three-day fast is a device that never settles
 * and a re-render every second for nothing. Under an hour it says minutes only,
 * because "0h 12m" reads like a broken clock.
 */
export function formatElapsed(hours) {
    if (!Number.isFinite(hours) || hours < 0)
        return 'Not started';
    const totalMinutes = Math.floor(hours * 60);
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    if (h === 0)
        return `${m}m`;
    return `${h}h ${m}m`;
}
/** The reference length that counts as a "full" dial when no target is set. */
export const DIAL_REFERENCE_HOURS = 72;
/**
 * Everything the dial needs, from the two numbers it has.
 *
 * Pure, and here rather than in `web/` for the usual reason: `web/` has no test
 * runner, and "progress against a target, intensity against a fixed reference"
 * is exactly the kind of distinction that gets quietly collapsed into one
 * number by somebody tidying up.
 */
export function fastDial(hours, targetHours = null, startingKetosis = null) {
    const safe = Number.isFinite(hours) && hours > 0 ? hours : 0;
    const target = targetHours !== null && targetHours > 0 ? targetHours : null;
    return {
        hours: safe,
        progress: Math.min(1, safe / (target ?? DIAL_REFERENCE_HOURS)),
        intensity: Math.min(1, safe / DIAL_REFERENCE_HOURS),
        stage: stageAt(safe, startingKetosis),
        targetMet: target !== null && safe >= target,
    };
}
/**
 * Common targets, for the picker. Hours.
 *
 * The progression the plan describes -- intermittent fasting up to a day, then
 * up to three -- rather than an arbitrary spread. A free-text number is still
 * accepted by the API; this is the shortcut list.
 */
export const FAST_TARGET_PRESETS = [
    { hours: 16, label: '16 hours' },
    { hours: 18, label: '18 hours' },
    { hours: 20, label: '20 hours' },
    { hours: 24, label: '24 hours (one day)' },
    { hours: 36, label: '36 hours' },
    { hours: 48, label: '48 hours (two days)' },
    { hours: 72, label: '72 hours (three days)' },
];
//# sourceMappingURL=fasting.js.map